// ============================================================
// Student Fee Collection Page
// ============================================================

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Button, Chip, Checkbox, Skeleton, Alert, Divider, MenuItem, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchool } from '@/hooks/useSchool';
import { useAuth } from '@/hooks/useAuth';
import { studentsApi } from '@/api/students.api';
import { useStudentFeeSummary, useStudentInstallments } from '@/hooks/useStudentFeeInstallments';
import { useCreateFeeReceipt } from '@/hooks/useFeeReceipts';
import { ReceiptModal } from './components/ReceiptModal';
import { StatusBadge } from '@/components/feedback/StatusBadge';
import { CalendarDays, CheckCircle2, Check, ArrowLeft, Receipt } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(Number(val))) return '₹0';
  const num = Number(val);
  const hasDecimals = Math.abs(num % 1) > 0.0001;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export function StudentFeeCollectionPage() {
  usePageMeta('Collect Fee', [
    { label: 'Fees', path: '/fees' },
    { label: 'Collect', path: '/fees/collect' },
    { label: 'Student Payment' },
  ]);

  const { studentId } = useParams();
  const navigate = useNavigate();
  const { currentSchool, schoolId } = useSchool();
  const { user } = useAuth();

  const [student, setStudent] = useState(null);
  const [isLoadingStudent, setIsLoadingStudent] = useState(true);

  // Fee data hooks
  const {
    data: feeSummary,
    isLoading: isLoadingSummary,
    refetch: refetchSummary,
  } = useStudentFeeSummary(studentId, { enabled: !!studentId });

  const {
    data: allInstallments = [],
    isLoading: isLoadingInstallments,
    refetch: refetchInstallments,
  } = useStudentInstallments(studentId, { enabled: !!studentId });

  const displayInstallments = useMemo(() => {
    return allInstallments;
  }, [allInstallments]);

  const { mutate: createReceipt, isPending: isSubmitting } = useCreateFeeReceipt();

  // Load student details
  useEffect(() => {
    if (schoolId && studentId) {
      studentsApi.getById(schoolId, studentId)
        .then((res) => setStudent(res?.data || res))
        .catch((err) => {
          console.error(err);
          toast.error("Failed to load student details");
        })
        .finally(() => setIsLoadingStudent(false));
    }
  }, [schoolId, studentId]);

  // Form State
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [installmentEdits, setInstallmentEdits] = useState({});
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [remarks, setRemarks] = useState('');
  const [receiptData, setReceiptData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // No default installment selection; let user choose explicitly

  const handleSelectionChange = (newSelectionModel) => {
    const added = newSelectionModel.filter(id => !selectedInstallments.includes(id));
    const removed = selectedInstallments.filter(id => !newSelectionModel.includes(id));

    setInstallmentEdits(prev => {
      const newEdits = { ...prev };
      removed.forEach(id => delete newEdits[id]);
      added.forEach(id => {
        const inst = displayInstallments.find(i => i.id === id);
        if (inst) {
          newEdits[id] = {
            paid_amount: inst.balance_amount,
            discount_amount: 0,
            fine_amount: 0
          };
        }
      });
      return newEdits;
    });
    setSelectedInstallments(newSelectionModel);
  };

  const handleEditChange = (id, field, value) => {
    const num = value === '' ? '' : Number(value);
    setInstallmentEdits((prev) => {
      const prevEdit = prev[id] || {};
      const newEdit = { ...prevEdit, [field]: num };

      if (field === 'paid_amount') {
        const inst = displayInstallments.find(i => i.id === id);
        if (inst) {
          // Clamp gross fee to inst.balance_amount
          newEdit.paid_amount = Math.min(Math.max(0, Number(num || 0)), inst.balance_amount);
        }
      }

      return {
        ...prev,
        [id]: newEdit
      };
    });
  };

  // Calculate Totals
  const totals = useMemo(() => {
    let totalGrossFee = 0;
    let totalDiscount = 0;
    let totalFine = 0;
    let totalNetPayable = 0;

    selectedInstallments.forEach(id => {
      const edits = installmentEdits[id] || {};
      const gross = Number(edits.paid_amount || 0);
      const disc = Number(edits.discount_amount || 0);
      const fine = Number(edits.fine_amount || 0);
      const net = Math.max(0, gross - disc + fine);

      totalGrossFee += gross;
      totalDiscount += disc;
      totalFine += fine;
      totalNetPayable += net;
    });

    return {
      totalPaid: totalNetPayable,
      totalDiscount,
      totalFine,
      totalFeeAmount: totalGrossFee,
      grandTotal: totalNetPayable,
    };
  }, [selectedInstallments, installmentEdits]);


  const handleOpenPreview = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (selectedInstallments.length === 0) {
      toast.error('Please select at least one installment to pay');
      return;
    }

    // Validate amounts
    let isValid = true;
    selectedInstallments.forEach(id => {
      const edits = installmentEdits[id];
      if (edits && edits.paid_amount <= 0 && edits.fine_amount <= 0) {
        toast.error('Payment amount cannot be zero');
        isValid = false;
      }
    });

    if (!isValid) return;
    setShowPreview(true);
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (selectedInstallments.length === 0) {
      toast.error('Please select at least one installment to pay');
      return;
    }

    // Validate amounts
    let isValid = true;
    selectedInstallments.forEach(id => {
      const edits = installmentEdits[id];
      if (edits.paid_amount <= 0 && edits.fine_amount <= 0) {
        toast.error('Payment amount cannot be zero');
        isValid = false;
      }
    });

    if (!isValid) return;

    const items = selectedInstallments.map((id) => {
      const gross = Number(installmentEdits[id]?.paid_amount || 0);
      const disc = Number(installmentEdits[id]?.discount_amount || 0);
      const fine = Number(installmentEdits[id]?.fine_amount || 0);
      const netPaid = Math.max(0, gross - disc + fine);

      return {
        student_fee_installment_id: id,
        paid_amount: netPaid,
        discount_amount: disc,
        fine_amount: fine,
      };
    });

    const payload = {
      student_id: studentId,
      payment_date: paymentDate,
      payment_mode: paymentMode,
      remarks: remarks || undefined,
      items,
    };

    createReceipt(payload, {
      onSuccess: (receipt) => {
        toast.success(`Payment successful! Receipt: ${receipt.receipt_no}`);
        setShowPreview(false);
        refetchSummary();
        refetchInstallments();

        const stu = student?.student || student;
        const enrollment = student?.enrollment;
        const studentName = `${stu?.first_name || ''} ${stu?.middle_name || ''} ${stu?.last_name || ''}`.replace(/\s+/g, ' ').trim() || stu?.student_name || 'Student';

        // Build enriched items with base feeAmount, discount, fine, and paid amounts
        const enrichedItems = selectedInstallments.map((id) => {
          const inst = displayInstallments.find((i) => i.id === id);
          const edits = installmentEdits[id] || {};
          const feeTypeName = inst?.fee_type?.name;
          const instName = inst?.installment_name || 'Installment';
          const description = feeTypeName ? `${feeTypeName} - ${instName}` : instName;
          const feeAmount = Number(inst?.balance_amount ?? inst?.original_amount ?? edits.paid_amount ?? 0);
          const discount = Number(edits.discount_amount || 0);
          const fine = Number(edits.fine_amount || 0);
          const paid = Number(edits.paid_amount ?? (feeAmount - discount + fine) ?? 0);

          return {
            student_fee_installment_id: id,
            installmentName: description,
            description,
            feeAmount,
            discount,
            discount_amount: discount,
            fine,
            fine_amount: fine,
            paying: paid,
            paid_amount: paid,
            amount: paid,
          };
        });

        setSelectedInstallments([]);
        setInstallmentEdits({});

        setReceiptData({
          ...receipt,
          items: enrichedItems,
          itemsDetails: enrichedItems,
          studentName,
          studentId: stu?.admission_no || stu?.admission_number || stu?.student_id,
          admissionNo: stu?.admission_no || stu?.admission_number || stu?.student_id,
          fatherName: stu?.father_name || stu?.parent?.father_name || stu?.guardian_name || '-',
          className: (enrollment?.class?.class_name || stu?.class?.class_name || stu?.class_name || '').replace(/^Class\s+/i, ''),
          sectionName: enrollment?.section?.section_name || stu?.section?.section_name || stu?.section_name || '',
          collectedBy: user?.name || user?.full_name || 'Admin',
        });
      },
      onError: (err) => {
        const msg = err?.response?.data?.detail || err?.message || 'Payment failed';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      },
    });
  };  const installmentColumns = useMemo(() => [
    {
      field: 'checkbox',
      headerName: '',
      width: 44,
      minWidth: 44,
      maxWidth: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const pendingOnly = displayInstallments.filter(i => (i.status || 'PENDING').toUpperCase() !== 'PAID');
        const allSelected = pendingOnly.length > 0 && selectedInstallments.length === pendingOnly.length;
        const someSelected = selectedInstallments.length > 0 && selectedInstallments.length < pendingOnly.length;
        return (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => {
              if (e.target.checked) {
                handleSelectionChange(pendingOnly.map(i => i.id));
              } else {
                handleSelectionChange([]);
              }
            }}
          />
        );
      },
      renderCell: (params) => {
        const inst = params.row;
        const isSelected = selectedInstallments.includes(inst.id);
        const isPayable = (inst.status || 'PENDING').toUpperCase() !== 'PAID';
        return (
          <Checkbox
            size="small"
            checked={!isPayable || isSelected}
            disabled={!isPayable}
            onChange={() => {
              const newSelection = isSelected
                ? selectedInstallments.filter(id => id !== inst.id)
                : [...selectedInstallments, inst.id];

              handleSelectionChange(newSelection);
            }}
          />
        );
      }
    },
    {
      field: 'installment_name',
      headerName: 'Fee Item / Installment',
      flex: 1.8,
      minWidth: 140,
      renderCell: (params) => {
        const feeTypeName = params.row.fee_type?.name;
        const instName = params.row.installment_name || 'Installment';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5 }}>
            {feeTypeName && (
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.2, mb: 0.25 }} noWrap>
                {feeTypeName}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>
              {instName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 85,
      renderCell: (params) => {
        const st = (params.row.status || 'PENDING').toUpperCase();
        let badgeStatus = st;
        if (st.includes('PARTIAL')) badgeStatus = 'PARTIAL';
        else if (st.includes('PENDING')) badgeStatus = 'UNPAID';
        else if (st.includes('PAID')) badgeStatus = 'PAID';

        return <StatusBadge status={badgeStatus} label={st} />;
      }
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      flex: 1,
      minWidth: 95,
      renderCell: (params) => {
        const dt = params.row.due_date;
        return <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>{dt ? dayjs(dt).format('DD MMM YYYY') : '-'}</Typography>;
      }
    },
    {
      field: 'paid_on',
      headerName: 'Paid On',
      flex: 1,
      minWidth: 95,
      renderCell: (params) => {
        const dt = params.row.paid_on || (params.row.status === 'PAID' ? params.row.updated_at : null);
        return <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8125rem' }}>{dt ? dayjs(dt).format('DD MMM YYYY') : '-'}</Typography>;
      }
    },
    {
      field: 'original_amount',
      headerName: 'Fee Amt (₹)',
      flex: 1,
      minWidth: 95,
      renderCell: (params) => <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>{formatCurrency(params.value)}</Typography>
    },
    {
      field: 'paid_amount',
      headerName: 'Paid (₹)',
      flex: 0.9,
      minWidth: 85,
      renderCell: (params) => <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>{formatCurrency(params.value)}</Typography>
    },
    {
      field: 'balance_amount',
      headerName: 'Balance (₹)',
      flex: 1,
      minWidth: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="error.main" noWrap sx={{ fontSize: '0.8125rem' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'discount_amount',
      headerName: 'Discount (₹)',
      flex: 1.1,
      minWidth: 95,
      sortable: false,
      renderCell: (params) => {
        const inst = params.row;
        const isSelected = selectedInstallments.includes(inst.id);
        const isPayable = (inst.status || 'PENDING').toUpperCase() !== 'PAID';
        
        if (!isPayable) {
          const discountVal = inst.discount_amount ?? inst.discount ?? 0;
          return <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.8125rem' }}>{discountVal ? formatCurrency(discountVal) : '-'}</Typography>;
        }

        const displayVal = installmentEdits[inst.id]?.discount_amount ?? (inst.discount_amount ?? inst.discount ?? '');

        return (
          <TextField
            size="small"
            type="number"
            disabled={!isSelected || !isPayable}
            value={displayVal}
            onChange={(e) => handleEditChange(inst.id, 'discount_amount', e.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': { height: 30, fontSize: '0.8125rem' },
              '& .MuiInputBase-input': { p: '4px 6px', textAlign: 'right' },
            }}
          />
        );
      }
    },
    {
      field: 'fine_amount',
      headerName: 'Fine (₹)',
      flex: 1.1,
      minWidth: 95,
      sortable: false,
      renderCell: (params) => {
        const inst = params.row;
        const isSelected = selectedInstallments.includes(inst.id);
        const isPayable = (inst.status || 'PENDING').toUpperCase() !== 'PAID';
        
        if (!isPayable) {
          const fineVal = inst.fine_amount ?? inst.fine ?? 0;
          return <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.8125rem' }}>{fineVal ? formatCurrency(fineVal) : '-'}</Typography>;
        }

        const displayVal = installmentEdits[inst.id]?.fine_amount ?? (inst.fine_amount ?? inst.fine ?? '');

        return (
          <TextField
            size="small"
            type="number"
            disabled={!isSelected || !isPayable}
            value={displayVal}
            onChange={(e) => handleEditChange(inst.id, 'fine_amount', e.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': { height: 30, fontSize: '0.8125rem' },
              '& .MuiInputBase-input': { p: '4px 6px', textAlign: 'right' },
            }}
          />
        );
      }
    },
    {
      field: 'paying_amount',
      headerName: 'Paying (₹)',
      flex: 1.2,
      minWidth: 105,
      sortable: false,
      renderCell: (params) => {
        const inst = params.row;
        const isSelected = selectedInstallments.includes(inst.id);
        const isPayable = (inst.status || 'PENDING').toUpperCase() !== 'PAID';
        
        if (!isPayable) {
          return <Typography variant="body2" color="text.secondary" fontWeight={500}>-</Typography>;
        }

        return (
          <TextField
            size="small"
            type="number"
            disabled={!isSelected || !isPayable}
            value={installmentEdits[inst.id]?.paid_amount ?? ''}
            onChange={(e) => handleEditChange(inst.id, 'paid_amount', e.target.value)}
            slotProps={{ htmlInput: { min: 0, max: inst.balance_amount, step: 'any' } }}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': { height: 30, fontWeight: 600, fontSize: '0.8125rem' },
              '& .MuiInputBase-input': { p: '4px 6px', textAlign: 'right' },
            }}
          />
        );
      }
    }
  ], [selectedInstallments, installmentEdits, displayInstallments]);

  if (isLoadingStudent) {
    return (
      <Box sx={{ pb: 6 }}>
        <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!student) {
    return (
      <Box sx={{ pb: 6 }}>
        <Alert severity="error">Student not found.</Alert>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate('/fees/collect')} sx={{ mt: 2 }}>
          Back to Directory
        </Button>
      </Box>
    );
  }

  const stu = student?.student || student;
  const parent = student?.parent || stu?.parent || {};
  const enrollment = student?.enrollment || stu?.enrollment || {};
  const studentName = `${stu?.first_name || ''} ${stu?.last_name || ''}`.trim() || stu?.student_name || 'Student';

  const admissionNo = stu?.admission_no || stu?.admission_number || '';
  const classNameRaw = enrollment?.class?.class_name || enrollment?.class?.name || stu?.class?.name || stu?.class?.class_name || stu?.class_name || '';
  const className = classNameRaw.toLowerCase().startsWith('class') ? classNameRaw : classNameRaw ? `Class ${classNameRaw}` : '';
  const sectionName = enrollment?.section?.section_name || enrollment?.section?.name || stu?.section?.name || stu?.section?.section_name || stu?.section_name || '';
  const classSectionStr = `${className} ${sectionName}`.trim();
  const fatherName = parent?.father_name || parent?.guardian_name || stu?.father_name || '-';
  const mobile = parent?.father_mobile || parent?.guardian_mobile || stu?.mobile || '-';

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title={`Collect Fee - ${studentName}`}
        subtitle={`${admissionNo} | ${classSectionStr} | Father: ${fatherName} | Mobile: ${mobile}`}
        action={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/fees/collect')}
          >
            Back to Directory
          </Button>
        }
      />

      <form onSubmit={handleOpenPreview}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Full Width Row 1: Fee Installments */}
          <WidgetContainer sx={{ p: { xs: 2, sm: 2.5, md: 2.5 }, height: 'auto' }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarDays size={20} color={tokens.colors.primary.main} />
              Fee Installments
            </Typography>

            {isLoadingInstallments ? (
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            ) : displayInstallments.length === 0 ? (
              <Alert severity="info" icon={<CheckCircle2 />}>No installments found for this student.</Alert>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 480,
                  overflow: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '& *': {
                    scrollbarWidth: 'none !important',
                    msOverflowStyle: 'none !important',
                  },
                  '& *::-webkit-scrollbar': {
                    display: 'none !important',
                    width: '0px !important',
                    height: '0px !important',
                  },
                }}
              >
                <DataGrid
                  rows={displayInstallments}
                  rowHeight={50}
                  columnHeaderHeight={44}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                  }}
                  getRowId={(row) => row.id}
                  columns={installmentColumns}
                  sx={{
                    border: 'none',
                    scrollbarWidth: 'none !important',
                    msOverflowStyle: 'none !important',
                    '& *': {
                      scrollbarWidth: 'none !important',
                      msOverflowStyle: 'none !important',
                    },
                    '& *::-webkit-scrollbar': {
                      display: 'none !important',
                      width: '0px !important',
                      height: '0px !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                      display: 'none !important',
                      opacity: '0 !important',
                      visibility: 'hidden !important',
                      pointerEvents: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar--vertical': {
                      display: 'none !important',
                      width: '0px !important',
                      visibility: 'hidden !important',
                    },
                    '& .MuiDataGrid-scrollbar--horizontal': {
                      display: 'none !important',
                      height: '0px !important',
                      visibility: 'hidden !important',
                    },
                    '& .MuiDataGrid-main': {
                      scrollbarWidth: 'none !important',
                      msOverflowStyle: 'none !important',
                      '&::-webkit-scrollbar': {
                        display: 'none !important',
                        width: '0px !important',
                        height: '0px !important',
                      },
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      scrollbarWidth: 'none !important',
                      msOverflowStyle: 'none !important',
                      '&::-webkit-scrollbar': {
                        display: 'none !important',
                        width: '0px !important',
                        height: '0px !important',
                      },
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                      scrollbarWidth: 'none !important',
                      msOverflowStyle: 'none !important',
                    },
                    '& .MuiDataGrid-virtualScrollerRenderZone': {
                      scrollbarWidth: 'none !important',
                      msOverflowStyle: 'none !important',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      borderBottom: `1px solid ${tokens.colors.border}`,
                    },
                    '& .MuiDataGrid-columnHeader': {
                      px: 0.75,
                      '&:focus, &:focus-within': { outline: 'none' },
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      px: 0.75,
                      '&:focus, &:focus-within': { outline: 'none' },
                    },
                  }}
                />
              </Box>
            )}
          </WidgetContainer>

          {/* Row 2: Payment Details & Summary */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'stretch' }}>
            <Box sx={{ flex: 2 }}>
              <WidgetContainer sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt size={20} color={tokens.colors.primary.main} />
                  Payment Details
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  <TextField
                    label="Payment Mode"
                    select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="Cheque">Cheque</MenuItem>
                    <MenuItem value="Card">Card</MenuItem>
                  </TextField>

                  <TextField
                    label="Payment Date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Box>

                <TextField
                  label="Remarks (Optional)"
                  multiline
                  rows={2}
                  fullWidth
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: `1px solid ${tokens.colors.border}`, pt: 3 }}>
                  <Button variant="outlined" onClick={() => navigate('/fees/collect')} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || selectedInstallments.length === 0}
                    startIcon={<Check size={18} />}
                  >
                    {isSubmitting ? 'Processing...' : `Confirm Payment: ${formatCurrency(totals.grandTotal)}`}
                  </Button>
                </Box>
              </WidgetContainer>
            </Box>

            {/* Right Column (Row 2): Summary & Current Payment */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Fee Summary Widget */}
              <WidgetContainer>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}>Fee Summary</Typography>

                  {isLoadingSummary ? (
                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                  ) : feeSummary ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Total Fee</Typography>
                        <Typography variant="body1" fontWeight={700}>{formatCurrency(feeSummary.total_fee)}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Total Paid</Typography>
                        <Typography variant="body1" fontWeight={700} color="success.main">{formatCurrency(feeSummary.paid)}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Balance Due</Typography>
                        <Typography variant="h6" fontWeight={800} color="error.main">{formatCurrency(feeSummary.balance)}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2">No summary available.</Typography>
                  )}
                </CardContent>
              </WidgetContainer>

              {/* Live Checkout Summary Box */}
              {selectedInstallments.length > 0 && (
                <WidgetContainer sx={{ p: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}>Current Payment</Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Fee Amount</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(totals.totalFeeAmount)}</Typography>
                    </Box>
                    {totals.totalFine > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="error.main">Fine Added</Typography>
                        <Typography variant="body2" fontWeight={600} color="error.main">+{formatCurrency(totals.totalFine)}</Typography>
                      </Box>
                    )}
                    {totals.totalDiscount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="success.main">Discount</Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">-{formatCurrency(totals.totalDiscount)}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight={700}>Net Payable</Typography>
                      <Typography variant="h5" fontWeight={800} color="primary.main">{formatCurrency(totals.grandTotal)}</Typography>
                    </Box>
                  </Box>
                </WidgetContainer>
              )}
            </Box>
          </Box>
        </Box>
      </form>

      {/* Payment Confirmation Preview Modal */}
      <Dialog open={showPreview} onClose={() => !isSubmitting && setShowPreview(false)} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Confirm Payment Details
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Student</Typography>
              <Typography variant="body2" fontWeight={600}>{studentName} ({admissionNo})</Typography>
              <Typography variant="caption" color="text.secondary">{classSectionStr}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Payment Mode</Typography>
              <Typography variant="body2" fontWeight={600}>{paymentMode}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Payment Date</Typography>
              <Typography variant="body2" fontWeight={600}>{dayjs(paymentDate).format('DD MMM YYYY')}</Typography>
            </Box>
            {remarks && (
              <Box>
                <Typography variant="caption" color="text.secondary">Remarks</Typography>
                <Typography variant="body2" fontWeight={600}>{remarks}</Typography>
              </Box>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Selected Installments</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: tokens.colors.background }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Installment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Fee Amt</TableCell>
                  {totals.totalDiscount > 0 && <TableCell align="right" sx={{ fontWeight: 600 }}>Discount</TableCell>}
                  {totals.totalFine > 0 && <TableCell align="right" sx={{ fontWeight: 600 }}>Fine</TableCell>}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Paying</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedInstallments.map((id) => {
                  const inst = displayInstallments.find((i) => i.id === id);
                  const edits = installmentEdits[id] || {};
                  const feeTypeName = inst?.fee_type?.name;
                  const instName = inst?.installment_name || 'Installment';
                  const displayName = feeTypeName ? `${feeTypeName} - ${instName}` : instName;
                  const gross = Number(edits.paid_amount || 0);
                  const disc = Number(edits.discount_amount || 0);
                  const fine = Number(edits.fine_amount || 0);
                  const net = Math.max(0, gross - disc + fine);

                  return (
                    <TableRow key={id}>
                      <TableCell sx={{ fontWeight: 500 }}>{displayName}</TableCell>
                      <TableCell>{inst?.due_date ? dayjs(inst.due_date).format('DD MMM YYYY') : '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(inst?.balance_amount || 0)}</TableCell>
                      {totals.totalDiscount > 0 && (
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          {disc > 0 ? `-${formatCurrency(disc)}` : '₹0'}
                        </TableCell>
                      )}
                      {totals.totalFine > 0 && (
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {fine > 0 ? `+${formatCurrency(fine)}` : '₹0'}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(net)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end', pr: 1 }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant="body2" color="text.secondary">Total Fee:</Typography>
              <Typography variant="body2" fontWeight={600}>{formatCurrency(totals.totalFeeAmount)}</Typography>
            </Box>
            {totals.totalDiscount > 0 && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography variant="body2" color="success.main">Total Discount:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">-{formatCurrency(totals.totalDiscount)}</Typography>
              </Box>
            )}
            {totals.totalFine > 0 && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography variant="body2" color="error.main">Total Fine:</Typography>
                <Typography variant="body2" fontWeight={600} color="error.main">+{formatCurrency(totals.totalFine)}</Typography>
              </Box>
            )}
            <Divider sx={{ width: '250px', my: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant="body1" fontWeight={700}>Net Payable:</Typography>
              <Typography variant="h6" fontWeight={800} color="primary.main">{formatCurrency(totals.grandTotal)}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setShowPreview(false)} disabled={isSubmitting}>
            Edit / Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={<Check size={18} />}
          >
            {isSubmitting ? 'Processing...' : `Confirm & Pay ${formatCurrency(totals.grandTotal)}`}
          </Button>
        </DialogActions>
      </Dialog>

      <ReceiptModal
        open={!!receiptData}
        onClose={() => setReceiptData(null)}
        receipt={receiptData}
        schoolInfo={currentSchool}
      />
    </Box>
  );
}

// ── Utility: Number to Words (INR) ────────────────────────────
function numberToWordsINR(num) {
  if (!num || num === 0) return 'Zero';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000)
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 100000)
      return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000)
      return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  return 'Rupees ' + convert(Math.floor(num));
}
